import React, { useState, useEffect } from "react";
import { useForm } from "react-hook-form";
import { Grid } from "@material-ui/core";
import { Button } from "react-bootstrap";
import { useDispatch } from "react-redux";
import { initLenderRequest, resetLenderRequest } from "../../../../../redux/actions/lenderRequestActions";
import { REQUEST_TYPES } from "../../../../shared/constants/RequestTypes";

const RequestTypeSelection = ({
  projectInfo,
  formState,
  incrementCounter,
  availableRequestTypes
}) => {
  const { handleSubmit, setValue, watch } = useForm({
    defaultValues: {
      requestType: ""
    }
  });

  const [selectedType, setSelectedType] = useState("");
  const [showValidation, setShowValidation] = useState(false);

  const dispatch = useDispatch();  

  // Watch form values
  const watchedRequestType = watch("requestType");

  useEffect(() => {
    if (availableRequestTypes.length === 1) {
      setSelectedType(availableRequestTypes[0]);
      setValue("requestType", availableRequestTypes[0]);
    }
  }, [availableRequestTypes]);

  // Restore form state if returning to this step
  useEffect(() => {
    if (formState?.requestType) {
      setSelectedType(formState.requestType);
      setValue("requestType", formState.requestType);
    }
  }, [formState, setValue]);

  // Update selected type when form value changes
  useEffect(() => {
    setSelectedType(watchedRequestType);
  }, [watchedRequestType]);

  const handleTypeSelect = (type) => {
    setSelectedType(type);
    setValue("requestType", type);
    setShowValidation(false);
  };

  const handleFormSubmit = (data) => {
    setShowValidation(true);

    if (data.requestType) {
      // If the request type has changed, reset all form state except comments. Otherwise, keep it all.
      const formStateData = formState?.requestType === data.requestType
        ? { 
          ...formState, 
          requestType: data.requestType 
        }
        : { 
          comments: formState?.comments || "" ,
          requestType: data.requestType
        };

      if (formState?.requestType !== data.requestType) {
        dispatch(resetLenderRequest());
        dispatch(initLenderRequest(data.requestType));
      }
      
      incrementCounter(formStateData);
    }
  };

  // if no project info or empty status, show loading indicator
  if (!projectInfo?.fnmaReviewStatus) {
    return (
      <Grid item xs={12} sm={12} md={10}>
        <h1>Loading Project Information...</h1>
      </Grid>
    );

    // If no request types are available, show message
  } else if (!availableRequestTypes?.length) {
    return (
      <Grid item xs={12} sm={12} md={10}>
        <Grid item xs={12} sm={12} md={10}>
          <h1>Select Request Type</h1>
        </Grid>
        <Grid item xs={12} style={{ marginTop: 20 }}>
          <h6>No Available Request Types</h6>
          <p>
            Based on the current Fannie Mae Review Status of "{projectInfo?.fnmaReviewStatus}", 
            no status review requests are available for this project at this time.
          </p>
          <button 
            type="button" 
            className="savebtn"
            onClick={() => window.history.back()}
            style={{ backgroundColor: "#6c757d" }}
          >
            Back to Project
          </button>
        </Grid>
      </Grid>
    );
  }

  return (
    <Grid item xs={12} sm={12} md={10}>
      <form onSubmit={handleSubmit(handleFormSubmit)}>
        <Grid item xs={12} sm={12} md={10}>
          <h1>Select Request Type</h1>
        </Grid>
        <Grid item xs={12} style={{ marginTop: 20 }}>
          <h6>Step 1 of 5 - Request Type Selection</h6>
          <p>
            Current Fannie Mae Review Status: <strong>{projectInfo?.fnmaReviewStatus}</strong>
          </p>
          <p>
            Please select the type of status review request you would like to submit:
          </p>
        </Grid>

        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={10}>
            <div>
              {availableRequestTypes.map((type) => (
                <div
                  key={type}
                  onClick={() => handleTypeSelect(type)}
                  style={{
                    border: selectedType === type ? "2px solid #1176b5" : "2px solid #e6e8ed",
                    borderRadius: "8px",
                    padding: "20px",
                    cursor: "pointer",
                    marginBottom: "15px",
                    backgroundColor: selectedType === type ? "#f8f9ff" : "white",
                    transition: "all 0.2s ease"
                  }}
                >
                  <>
                    <h3 style={{ margin: "0 0 8px 0", color: "#333", fontSize: "18px" }}>{type}</h3>
                    <p style={{ margin: "0", color: "#666", fontSize: "14px" }}>
                      {type === REQUEST_TYPES.REMOVE_UNAVAILABLE_STATUS 
                        ? "Request to remove the project’s Unavailable status. Fannie Mae will review the request and determine the final status."
                        : type === REQUEST_TYPES.ADD_UNAVAILABLE_STATUS 
                        ? "Request to update the project’s status to Unavailable. You will be prompted to select the applicable reason code(s). Fannie Mae will review the request and determine the final status."
                        : type === REQUEST_TYPES.MODIFY_UNAVAILABLE_STATUS 
                        ? "Request to add or remove reason code(s) without changing the project’s Unavailable status. Fannie Mae will review the request and determine the final reason codes."
                        : <></>}
                    </p>
                  </>
                </div>
              ))}
            </div>

            {showValidation && !selectedType && (
              <div style={{ color: "#d02f2f", marginTop: "10px", fontWeight: "bold" }}>
                Please select a request type to continue.
              </div>
            )}
          </Grid>
        </Grid>

        <Grid container spacing={2} className="dialoguClass">
          <Grid item xs={12} sm={12} md={10}>
            <Grid container justifyContent="space-between">
              <Button
                type="submit"
                className="back-button"
                style={{width: "150px"}}
                onClick={() => window.history.back()}
                form="addProjectForm"
              >
                Back to Project
              </Button>
              <Button
                type="submit"
                disabled={!selectedType}
                className={"btn-submit" + (!selectedType ? " btn-invalid" : "")}
              >
                Next
              </Button>            
            </Grid>
          </Grid>
        </Grid>
      </form>
    </Grid>
  );
};

export default RequestTypeSelection;